/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import {
    Component,
    Input,
} from '@angular/core';

import { MatchOperation } from 'generated-types';
import { L10nService } from '@vmw/ngx-vip';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { GeoMatchConfigItem } from 'ajs/modules/match/factories/geo-match.config-item.factory';

import {
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories/repeated-message-item.factory';

import * as l10n from './geo-match-readonly.component.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Match object containing properties needed by match-display-wrapper.
 */
interface IFormattedMatch {
    criteria: string;
    attribute: string;
    value?: string;
}

/**
 * @description
 *     Readonly Match component for Geo Match.
 * @author Nisar Nadaf
 */
@Component({
    selector: 'geo-match-readonly',
    templateUrl: './geo-match-readonly.component.html',
})
export class GeoMatchReadonlyComponent {
    /**
     *  Geo Match config item instance.
     */
    @Input()
    public editable: RepeatedMessageItem<GeoMatchConfigItem>;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    constructor(
        l10nService: L10nService,
        private readonly schemaService: SchemaService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Returns list of matches, formatted for repeated element list.
     *
     * Need to have this as a getter instead of initialising it in a lifecylce hook because
     * when a user edit the expanded rule and updates it the value in expanded
     * grid is not getting updated.
     */
    public get formattedMatches(): IFormattedMatch[] {
        return this.editable.config.map((geoMatch: GeoMatchConfigItem) => {
            const {
                attribute,
                match_operation: matchCriteria,
                values,
            } = geoMatch.config;

            return {
                criteria: this.getMatchCriteria(matchCriteria),
                attribute: this.getMatchAttributeLabel(attribute),
                value: values.join(', '),
            };
        });
    }

    /**
     * Used in *ngFor.
     */
    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Method to get text label for given match criteria.
     */
    private getMatchCriteria(matchCriteria: MatchOperation): string {
        return this.schemaService.getEnumValueLabel(
            'MatchOperation',
            matchCriteria,
        );
    }

    /**
     * Method to get text label for GeoMapping Enum Attributes.
     */
    private getMatchAttributeLabel(attribute: string): string {
        return this.schemaService.getEnumValue(
            'GeoMappingAttribute',
            attribute,
        ).label || '';
    }
}
